import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import { convertToSqFt, ImperialSystem, UnitSystem } from "@my-dld/utils/UnitUtils";
import React, { useEffect, useState } from "react";
import { Api as BcsApi, DTO as BcsDTO } from "@10x/bcs";
import { TabuPropertyType } from "@my-dld/utils/PropertyUtils";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { CurrencyUtil, LocaleUtil, StringUtil } from "@eres/core";
import { RentalRange } from "./RentalRange";
import { PropertyDrawing } from "./PropertyDrawing";
import { DetailsKV, extractPropInfo } from "@my-dld/screens/MyDLD/PropertyDetails";
import { DetailsKVProps } from "@eres/ejari-web/types";
import BcsRating from "../BCSRating";
import { AppConstants } from "../../configApp";
import { ProjectInfo } from "./ProjectInfo";
import { getLocalValue } from "@eres/core/dist/LocaleValue";
import { FixCapNameX } from "@my-dld/utils/Utils";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { checkBlockListOnProperty, Reasons } from "@my-dld/tabuapi";
import { showInfo } from "@my-dld/utils/UIUtils";
import { getAppServices } from "@10x/coreapp";
import { propertiesId } from "@my-dld/screens/MyDLD/FreeholdProperties/PropertyIDs";


export function Details( props: { data: AssetCard | any; unit: UnitSystem; onBCSReValuate?: () => void } ) {
	const { data, unit, onBCSReValuate, ...otherProps } = props;
	const [ bcsRating, setRating ] = useState<BcsDTO.BCSRating | undefined>( undefined );
	const [showFreeholdMessage , setShowFreeholdMessage ] = useState(false);
	const [ blockLoading, setBlockLoading ] = useState( true );
	const [ blockReason, setBlockReason ] = useState<Reasons[]>( []);

	useEffect( () => {
		checkBlockListOnProperty(data.propertyId + "").then(rep => {
			setBlockLoading(false);
			if (rep && rep.Data && rep.Data.length > 0) {
				setBlockReason(rep.Data);
			}
		}).catch(_ => {
			setBlockLoading(false);
		});
	}, [data] );
	useEffect( () => {
		if ( pType === TabuPropertyType.UNIT ) {
			const buildingId = data.buildingPropertyId;
			if ( buildingId ) {
				let requestData: BcsDTO.PropertyRatingRequest = {
					requestType: BcsDTO.RatingType.Tabu,
					propertyId: buildingId,
					propertyTypeId: TabuPropertyType.BUILDING,
					propertySubTypeId: 2,
					isFreeHold: data.isFreeHold,
					dmNumber: data.municipalityNo,
				};
				BcsApi.getBCSRating( requestData ).then( result => {
					setRating( result );
				} ).catch( reason => {
					setRating( undefined );
				} );
			}
		}

		if (propertiesId.includes(data.tabuPropertyId)) {
			setShowFreeholdMessage(true);
		}

	}, [] );

	const pType = data.propertyTypeId;
	let ownerArea: string | number | undefined = extractOwnedArea( data );
	let size = data.size.squareMeter;
	if ( unit.Id === ImperialSystem.Id ) {
		size = data.size.squareFeet;
		if ( ownerArea ) ownerArea = convertToSqFt( Number( ownerArea ) );
	}
	let landNo = data.municipalityNo;
	if ( landNo.startsWith( "-" ) ) landNo = landNo.slice( 1 );
	let builtupArea = 0;
	let commonArea = 0;
	let suitArea = 0;
	let balconyArea = 0;
	if ( pType === TabuPropertyType.VILLA || pType === TabuPropertyType.BUILDING ) {
		builtupArea = data.builtupArea;
		commonArea = data.commonArea;
		if ( unit.Id === ImperialSystem.Id ) {
			builtupArea = convertToSqFt( Number( builtupArea ) );
			commonArea = convertToSqFt( Number( commonArea ) );
		}
	} else if ( pType === TabuPropertyType.UNIT ) {
		suitArea = data.suitArea;
		balconyArea = data.balconyArea;
		commonArea = data.commonArea;
		if ( unit.Id === ImperialSystem.Id ) {
			builtupArea = convertToSqFt( Number( builtupArea ) );
			suitArea = convertToSqFt( Number( suitArea ) );
			balconyArea = convertToSqFt( Number( balconyArea ) );
			commonArea = convertToSqFt( Number( commonArea ) );
		}
	}
	let { share, inValue, val, lbl } = getPropData( data );
	const company = data?.agentCompany;
	const companyName = company ? FixCapNameX( getLocalValue( company.name ) ) : undefined;
	let blockView;
	if (blockReason && !blockLoading && blockReason.length > 0) {
		blockView = <BlockReasonView label={ Strings.is_property_blocked } value={Strings.yes.trim()} onResonClick={() => {
			let x = "";
			x = blockReason.map( m => m.BlockReason ).join( "\n\n" );
			showInfo( x, {title: LVStrings.blocking_reason, icon: "info"} );
		}}/>
	}

	return (<>
		{showFreeholdMessage && <div className="alert text-success max-content gap-2 d-flex align-items-center p-0">
			<i className="fas fa-info-circle fs-6"></i> {Strings.transfer_property}
		</div>}
		<div className="flex-grid flex-order">
			{ !isEmptyStringOrNull( companyName ) &&
				<DetailsKV label={ Strings.owner_company } value={ companyName }/>
			}
			<DetailsKV label={ lbl } value={ val }/>
			{blockView}
			<ProjectInfo propertyId={ data.propertyId }/>
			{ pType === TabuPropertyType.LAND &&
				<React.Fragment>
					<DetailsKV label={ Strings.property_status.replace( "<br/>", "" ) } value={ data.propertyStatus }/>
					<DetailsKV label={ Strings.area_label } value={ data.area }/>
					<DetailsKV label={ Strings.land_num_label } value={ data.landNo }/>
					<DetailsKV label={ Strings.size } value={ size }/>
					{
						ownerArea &&
						<DetailsKV label={ Strings.owned_area } value={ ownerArea }/>
					}
					<DetailsKV label={ Strings.land_mun_number } value={ landNo }/>
					<DetailsKV label={ Strings.land_type } value={ data.landType }/>
				</React.Fragment>
			}
			{ ( pType === TabuPropertyType.VILLA || pType === TabuPropertyType.BUILDING ) &&
				<React.Fragment>
					<DetailsKV label={ Strings.property_status.replace( "<br/>", "" ) } value={ data.propertyStatus }/>
					<DetailsKV label={ Strings.area_label } value={ data.area }/>
					<DetailsKV label={ Strings.size } value={ size }/>
					{
						ownerArea &&
						<DetailsKV label={ Strings.owned_area } value={ ownerArea }/>
					}
					<DetailsKV label={ Strings.land_mun_number } value={ data.municipalityNo }/>
					<DetailsKV label={ Strings.built_up_area } value={ builtupArea }/>
					<DetailsKV label={ Strings.common_area } value={ commonArea }/>
					<DetailsKV label={ Strings.room_count } value={ data.roomCount }/>
					<DetailsKV label={ Strings.floor_count } value={ data.floorCount }/>
				</React.Fragment>
			}
			{ pType === TabuPropertyType.UNIT &&
				<React.Fragment>
					<DetailsKV label={ Strings.property_status.replace( "<br/>", "" ) } value={ data.propertyStatus }/>
					<DetailsKV label={ Strings.area_label } value={ data.area }/>
					<DetailsKV label={ Strings.building_name } value={ data.buildingName }/>
					<DetailsKV label={ Strings.floor_no } value={ data.floorUnitNumber }/>
					<RatingDetailsKV
						label={ Strings.star_building_rating } rating={ bcsRating }
						value={ data.buildingName } onBCSReValuate={ onBCSReValuate }/>
					<DetailsKV label={ Strings.size } value={ size }/>
					{
						ownerArea &&
						<DetailsKV label={ Strings.owned_area } value={ ownerArea }/>
					}
					<DetailsKV label={ Strings.land_mun_number } value={ data.municipalityNo }/>
					<DetailsKV label={ Strings.rooms } value={ LocaleUtil.getLocalValue( data.roomsName ) }/>
					<DetailsKV label={ Strings.suit_area } value={ suitArea }/>
					<DetailsKV label={ Strings.balcony_area } value={ balconyArea }/>
					<DetailsKV label={ Strings.common_area } value={ commonArea }/>
				</React.Fragment>
			}
			<RentalRange data={ data }/>
			<PropertyDrawing data={ data }/>
			{
				data.serviceChargeIndex !== undefined && data.serviceChargeIndex !== 0 &&
				<DetailsKV
					label={ Strings.service_charge_index }
					value={ data.serviceChargeIndex + " " + Strings.aed + " / " + Strings.sq_ft }/>
			}
			<DetailsKV label={ Strings.purchase_price } value={ inValue }/>
			<DetailsKV label={ Strings.ownership } value={ share + " %" }/>
			<DetailsKV
				label={ Strings.freehold } value={ data.isFreeHold ? Strings.yes : Strings.no }
				valueClassName={ `${ data.isFreeHold ? "yes" : "no" }-label` }/>
			<DetailsKV
				label={ Strings.granted } value={ data.isGranted ? Strings.yes : Strings.no }
				valueClassName={ `${ data.isGranted ? "yes" : "no" }-label` }/>
		</div>
	</>);
}

function BlockReasonView( props: DetailsKVProps & {onResonClick: () => void } ) {
	let { label, value, onResonClick } = props;
	return <div>
		<span>{ label }</span>
		<span><span className="yes-label">{value} </span> <button
			className="btn btn-sm" onClick={onResonClick}>
			<i className="fa fa-info-circle mx-3"></i>
		</button></span>
	</div>
}

function RatingDetailsKV( props: DetailsKVProps & { rating?: BcsDTO.BCSRating; onBCSReValuate?: () => void } ) {
	let value = "-";
	let { label, rating, value: value1, valueRenderer, onBCSReValuate } = props;
	if ( !rating ) return null;
	if ( value1 ) {
		value = LocaleUtil.getLocalValue( value1 );
		if ( StringUtil.isEmptyStringOrNull( value ) ) {
			value = "-";
		}
	}
	const v = valueRenderer ? valueRenderer() : value;
	return <div className="bcs-building-rating">
		<span>{ label }</span>
		<BcsRating rating={ rating } propertyName={ value } onReValuate={ onBCSReValuate }/>
	</div>;
}

function getPropData( property: AssetCard ) {
	let share: string | number = property.shareValue;
	if ( share > 100 ) share = 100;
	share = share?.toFixed( 2 ) + "";
	let inValue: string | number | null = property.investmentValue;
	if ( inValue == null || inValue <= 0 )
		inValue = "-";
	else
		inValue = CurrencyUtil.toFormattedCurrency( inValue );
	if ( inValue.lastIndexOf( "." ) > -1 ) inValue = inValue.substr( 0, inValue.lastIndexOf( "." ) );
	let { val, lbl, icon } = extractPropInfo( property );
	return { share, inValue, val, lbl, icon };
}

export function Header( props: { property: AssetCard } ) {
	const { property } = props;
	let { share, inValue, val, lbl, icon } = getPropData( property );
	return <div className="property-info-summary">
		<div>
			<div className="property-icon">
				<img src={ AppConstants.rootUrl + "assets/property-icons/" + icon + ".svg" }/>
			</div>
		</div>
		<div className="seperator-v"/>
		<div>
			<span>{ val }</span>
			<span>{ lbl }</span>
		</div>
		<div className="seperator-v"/>
		<div>
			<span>{ inValue }</span>
			<span>{ Strings.purchase_price }</span>
		</div>
		<div className="seperator-v"/>
		<div>
			<span>{ share + " %" }</span>
			<span>{ Strings.ownership }</span>
		</div>
		<div className="seperator-v"/>
		<div>
			<span>{ property.isFreeHold ? Strings.yes : Strings.no }</span>
			<span>{ Strings.freehold }</span>
		</div>
		<div className="seperator-v"/>
		<div>
			<span>{ property.isGranted ? Strings.yes : Strings.no }</span>
			<span>{ Strings.granted }</span>
		</div>
	</div>;
}

function extractOwnedArea( data: any ) {
	let shareValuePer: number = 0;
	if ( data.shareValue ) {
		let shareValue = "";
		shareValuePer = Number( data.shareValue.toFixed( 2 ) );
		if ( shareValuePer < 100 ) {
			let OwnedSize = ( data.shareValue / 100 ) * extractUnitValue( data );
			shareValue = `${ Number( OwnedSize.toFixed( 2 ) ).toString() }`;
			return shareValue;
		}
	}
	return undefined;
}

function extractUnitValue( data: any ) {
	return data.size.squareMeter;
}
